/**
 * TonkDealAnim — one-shot flying-card overlay triggered when a hand starts.
 *
 * When `active` is true, computes the on-screen positions of the dealer
 * seat and each active recipient seat (via `data-testid` lookups), then
 * animates 5 face-down cards from the dealer to each recipient in a
 * staggered burst (~1.5s total). Deals in "one at a time going clockwise"
 * order starting from the winner (turn_index) — matches the house rule
 * "the winner gets cards first."
 *
 * The overlay is fixed to the viewport with `pointer-events: none` so it
 * never interferes with clicks. It self-clears via `onComplete` when the
 * final card lands.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Belt-and-suspenders CSS: after each card's animation window ends, this
// keyframe forces visibility → hidden even if Framer Motion / React never
// unmounts the node. Injected once at module load. Prevents the "random
// face-down cards floating on the felt" bug seen on iOS Safari when the
// tab is backgrounded mid-deal and setTimeout is throttled.
if (typeof document !== 'undefined' && !document.getElementById('tonk-card-force-hide-kf')) {
    const style = document.createElement('style');
    style.id = 'tonk-card-force-hide-kf';
    style.textContent = `
        @keyframes tonk-card-force-hide {
            to { opacity: 0; visibility: hidden; }
        }
    `;
    document.head.appendChild(style);
}

const CARD_W = 32;
const CARD_H = 44;
const CARD_STAGGER = 0.075;     // seconds between card send-offs
const CARD_FLIGHT = 0.45;       // seconds each card is in flight
const HAND_SIZE = 5;

function rectCenter(el) {
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

/**
 * Build the flight plan the first time we mount. Positions come from the
 * DOM refs at THIS moment — if the felt gets remeasured mid-animation the
 * cards still travel to their original targets, which is fine.
 */
function buildFlights({ dealerSeatIndex, recipientSeatIndices, mySeatIndex }) {
    // Dealer origin: their seat pill on the felt, OR my own hand row
    // if I'm the dealer.
    const dealerNode = dealerSeatIndex === mySeatIndex
        ? document.querySelector('[data-testid="tonk-open-avatar-picker"]')
        : document.querySelector(`[data-testid="tonk-seat-${dealerSeatIndex}"]`);
    const dealerAt = rectCenter(dealerNode);
    if (!dealerAt) return [];

    // Resolve each recipient's landing spot
    const recipients = recipientSeatIndices
        .map((idx) => {
            const node = idx === mySeatIndex
                ? document.querySelector('[data-testid="tonk-hand-total"]')
                : document.querySelector(`[data-testid="tonk-seat-${idx}"]`);
            return { seat: idx, at: rectCenter(node) };
        })
        .filter((r) => r.at);

    if (recipients.length === 0) return [];

    // 5 rounds clockwise from `recipients[0]` (the winner / first-to-act)
    const flights = [];
    for (let round = 0; round < HAND_SIZE; round += 1) {
        for (let r = 0; r < recipients.length; r += 1) {
            const dst = recipients[r].at;
            flights.push({
                key: `r${round}-s${recipients[r].seat}`,
                from: dealerAt,
                to: dst,
                delay: (round * recipients.length + r) * CARD_STAGGER,
                targetSeat: recipients[r].seat,
            });
        }
    }
    return flights;
}

export default function TonkDealAnim({
    active,
    dealerSeatIndex,
    recipientSeatIndices,
    mySeatIndex,
    onComplete,
}) {
    const [flights, setFlights] = useState([]);

    useEffect(() => {
        if (!active) return undefined;
        // Wait a frame so the seat DOM has painted after `hand_started`
        const t = window.requestAnimationFrame(() => {
            setFlights(buildFlights({
                dealerSeatIndex, recipientSeatIndices, mySeatIndex,
            }));
        });
        return () => window.cancelAnimationFrame(t);
    }, [active]);

    // Compute a total duration so the parent knows when to clear
    const totalDur = useMemo(() => {
        if (!flights.length) return 0;
        const lastDelay = flights[flights.length - 1].delay;
        return (lastDelay + CARD_FLIGHT) * 1000 + 100;
    }, [flights]);

    // Store onComplete in a ref so parent re-renders don't reset the
    // cleanup timer below. Previously, every parent state change (WS
    // messages, timer ticks) would cancel + restart the timeout,
    // starving the cleanup and letting the cards visually linger.
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    useEffect(() => {
        if (!active || !flights.length) return undefined;
        const t = setTimeout(() => {
            const cb = onCompleteRef.current;
            if (cb) cb();
            setFlights([]);
        }, totalDur);
        return () => clearTimeout(t);
    }, [active, flights, totalDur]);

    return (
        <div className="fixed inset-0 pointer-events-none z-40" aria-hidden="true">
            <AnimatePresence>
                {active && flights.map((f) => (
                    <motion.div
                        key={f.key}
                        initial={{
                            x: f.from.x - CARD_W / 2,
                            y: f.from.y - CARD_H / 2,
                            opacity: 0,
                            scale: 0.65,
                            rotate: 0,
                        }}
                        animate={{
                            x: f.to.x - CARD_W / 2,
                            y: f.to.y - CARD_H / 2,
                            opacity: [0, 1, 1, 0],
                            scale: [0.65, 1, 1, 0.7],
                            rotate: [-5, 12, -3, 0],
                        }}
                        transition={{
                            delay: f.delay,
                            duration: CARD_FLIGHT,
                            times: [0, 0.2, 0.75, 1],
                            ease: 'easeOut',
                        }}
                        style={{
                            position: 'absolute',
                            width: CARD_W,
                            height: CARD_H,
                            borderRadius: 4,
                            border: '1.5px solid #000',
                            background:
                                'repeating-linear-gradient(45deg, #C81E29 0 4px, #8A0F17 4px 8px)',
                            boxShadow: '0 3px 8px rgba(0,0,0,0.55)',
                            top: 0, left: 0,
                            pointerEvents: 'none',
                            // Belt-and-suspenders: after the animation's total
                            // runtime, if React hasn't yet unmounted us (e.g.
                            // Safari throttled the cleanup timeout while the
                            // tab was backgrounded), force the card invisible.
                            // Prevents the "random face-down cards floating
                            // on the felt" bug reported 2026-07-22.
                            animation: `tonk-card-force-hide 0ms ${(f.delay + CARD_FLIGHT) * 1000}ms forwards`,
                        }}
                    />
                ))}
            </AnimatePresence>
        </div>
    );
}
